/**
 * Hook for accessing user's owned/purchased products
 * Retrieves data from IndexedDB cache (synced from GCS)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getOwnedProductsFromDB, OwnedProduct } from '../services/indexedDB';

interface UseOwnedProductsReturn {
  /** List of owned products */
  ownedProducts: OwnedProduct[];
  /** Loading state */
  loading: boolean;
  /** Check if a product is owned by ID */
  isOwned: (productId: string) => boolean;
  /** Get owned product details by ID (returns undefined if not owned) */
  getOwnedProduct: (productId: string) => OwnedProduct | undefined;
  /** Set of owned product IDs for fast lookup */
  ownedProductIds: Set<string>;
  /** Refresh owned products from IndexedDB */
  refresh: () => Promise<void>;
}

/**
 * Hook to access the current user's owned products
 * Products are cached in IndexedDB and synced from GCS on login
 */
export function useOwnedProducts(): UseOwnedProductsReturn {
  const { currentUser } = useAuth();
  const [ownedProducts, setOwnedProducts] = useState<OwnedProduct[]>([]);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch owned products from IndexedDB
   */
  const fetchOwnedProducts = useCallback(async () => {
    if (!currentUser) {
      setOwnedProducts([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const products = await getOwnedProductsFromDB();
      setOwnedProducts(products);
    } catch (error) {
      console.error('[useOwnedProducts] Failed to fetch owned products:', error);
      setOwnedProducts([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Fetch on mount and when user changes
  useEffect(() => {
    fetchOwnedProducts();
  }, [fetchOwnedProducts]);

  // Create a Set of owned product IDs for O(1) lookup
  const ownedProductIds = useMemo(() => {
    return new Set(ownedProducts.map(p => p.id));
  }, [ownedProducts]);

  /**
   * Check if a product is owned
   */
  const isOwned = useCallback((productId: string): boolean => {
    return ownedProductIds.has(productId);
  }, [ownedProductIds]);

  /**
   * Get owned product details by ID
   */
  const getOwnedProduct = useCallback((productId: string): OwnedProduct | undefined => {
    return ownedProducts.find(p => p.id === productId);
  }, [ownedProducts]);

  return {
    ownedProducts,
    loading,
    isOwned,
    getOwnedProduct,
    ownedProductIds,
    refresh: fetchOwnedProducts,
  };
}

export default useOwnedProducts;
